import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import { getMessage } from 'helpers/helper';
import useImageTemplates from 'hooks/useImageTemplates';
import TemplateImageModal from 'components/common/templateImageModal';
import UploadIcon from '@mui/icons-material/Upload';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import wineSpecsEmptyProfileAndWine from 'assets/wineSpecsEmptyProfile&Wine.png';
import wineSpecsEmptyTastingNotes from 'assets/wineSpecsEmptyTestingNote.png';
import wineSpecsEmptyPairingNotes from 'assets/wineSpecsEmptyPairingNote.png';

import wineStore from 'store/wineStore';
import FS from 'services/fileService';

import M from 'messages';
import classes from './styles';

const WineSpecs = observer(({ data, label, disabled }) => {
  const { enqueueSnackbar } = useSnackbar();
  const itemFieldPlaceHoldersEnum = {
    'Profile & Story': M.get('menuItem.wineSpecSection.profileStoryPlaceholder'),
    'Tasting Notes': M.get('menuItem.wineSpecSection.tastingNotesPlaceholder'),
    'Pairing Recommendation': M.get('menuItem.wineSpecSection.pairingNotesPlaceholder'),
  };

  const defaultImagesEnum = {
    'Profile & Story': wineSpecsEmptyProfileAndWine,
    'Tasting Notes': wineSpecsEmptyTastingNotes,
    'Pairing Recommendation': wineSpecsEmptyPairingNotes,
  };

  const [expanded, setExpanded] = useState({});
  const [uploading, setUploading] = useState({});
  const imageInputRefs = useRef({});

  const { open, openModal, closeModal, currentTemplates, currentLoading, currentSection } = useImageTemplates();
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleChange = panel => (event, isExpanded) => {
    setExpanded({ ...expanded, [panel]: isExpanded });
  };

  const handleTriggerFileUpload = index => {
    const inputRef = imageInputRefs.current[`input-${index}`];
    if (inputRef) {
      inputRef.click();
    }
  };

  const handleDeleteImage = (index, event) => {
    event.stopPropagation();
    wineStore.onChangeWineSpec(
      {
        target: { value: null },
      },
      index,
      'image',
    );
  };

  const handleSelectTemplate = (template, index) => {
    // Set the template URL as the image
    // Template from API has structure: { url, key, name }
    const templateUrl = template.url || template;
    wineStore.onChangeWineSpec(
      {
        target: { value: templateUrl },
      },
      index,
      'image',
    );
  };

  return (
    <Box sx={classes.root} mt={1}>
      {data.map((item, index) => {
        const panelId = `panel-${index}`;
        const isExpanded = expanded[panelId] || false;
        const hasNoDescription = !item.description || item.description.trim() === '';

        return (
          <Accordion
            key={index}
            expanded={isExpanded}
            onChange={handleChange(panelId)}
            sx={{
              margin: '0px',
              '&.MuiAccordion-root': classes.accordionRoot,
              '&.Mui-expanded': classes.accordionExpanded,
            }}>
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}>
              <Box sx={classes.summaryContent}>
                <Typography>{item.title || label}</Typography>
                {!isExpanded && hasNoDescription && (
                  <Typography sx={classes.noDetailsText}>{M.get('menuItem.wineSpecSection.noDetails')}</Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 6, xl: 5 }}>
                  <Box sx={classes.imageSection}>
                    <Box sx={classes.buttonGroup}>
                      <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => handleTriggerFileUpload(index)}
                        disabled={disabled}
                        sx={classes.uploadButton}>
                        {M.get('menuItem.wineSpecSection.addWineryImage')}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={() => {
                          const section = item.title || label;
                          setSelectedIndex(index);
                          openModal(section);
                        }}
                        disabled={disabled}
                        sx={classes.templateButton}>
                        {M.get('menuItem.wineSpecSection.changeTemplate')}
                      </Button>
                    </Box>
                    <Box sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
                      <input
                        ref={el => {
                          if (el) imageInputRefs.current[`input-${index}`] = el;
                        }}
                        type="file"
                        accept="image/*"
                        onChange={async e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploading({ ...uploading, [index]: true });
                            try {
                              const response = await FS.upload(file, 'wineSpec');
                              const location = Array.isArray(response.data.response_data)
                                ? response.data.response_data.find(i => i.originalImage)?.Location ||
                                  response.data.response_data[0]?.Location
                                : response.data.response_data?.Location;
                              wineStore.onChangeWineSpec(
                                {
                                  target: { value: location },
                                },
                                index,
                                'image',
                              );
                            } catch (error) {
                              enqueueSnackbar(getMessage(error?.response?.data, 'error'), {
                                variant: 'error',
                              });
                            } finally {
                              setUploading({ ...uploading, [index]: false });
                              // Reset input
                              if (imageInputRefs.current[`input-${index}`]) {
                                imageInputRefs.current[`input-${index}`].value = '';
                              }
                            }
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </Box>
                    {uploading[index] ? (
                      <Box sx={classes.loadingContainer}>
                        <CircularProgress size={40} />
                      </Box>
                    ) : item.image ? (
                      <Box sx={classes.previewContainer}>
                        {!disabled && (
                          <IconButton
                            sx={classes.deleteImageButton}
                            onClick={e => handleDeleteImage(index, e)}
                            size="small"
                            aria-label="delete image">
                            <DeleteIcon />
                          </IconButton>
                        )}
                        <Box component="img" src={item.image} alt="Preview" sx={classes.previewImage} />
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={defaultImagesEnum[item.title || label]}
                        alt="Preview"
                        sx={classes.previewImage}></Box>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, lg: 6, xl: 7 }}>
                  <TextField
                    multiline
                    rows={10}
                    variant="outlined"
                    id="description"
                    name="description"
                    disabled={disabled}
                    label={itemFieldPlaceHoldersEnum[item.title || label]}
                    value={item?.description}
                    onChange={event => wineStore.onChangeWineSpec(event, index, 'description')}
                    sx={classes.descField}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
      <TemplateImageModal
        open={open}
        onSelect={template => {
          if (selectedIndex !== null) {
            handleSelectTemplate(template, selectedIndex);
          }
        }}
        templates={currentTemplates}
        loading={currentLoading}
        onClose={closeModal}
        title={currentSection ? M.get('menuItem.templateModal.title').replace('{section}', currentSection) : ''}
      />
    </Box>
  );
});

WineSpecs.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  label: PropTypes.string.isRequired,
};

export default WineSpecs;
